'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  useCommerceSource,
  type Cart,
  type CartAddition,
  type CartFulfilment,
  type CartLine,
  type DecimalString,
} from './commerceSource';

/**
 * The shopper's basket, shared by every block on the page.
 *
 * <p>A basket is one thing across a site — an Add on a product page and the
 * lines on the cart page are the same basket — so it lives here rather than in
 * each block. Blocks call {@link useCart} and never touch the source directly.
 *
 * <h3>The token is a credential</h3>
 *
 * <p>A guest has no account, so the cart token IS the identity of their basket:
 * anyone holding it holds the basket. It is kept in `localStorage`, which is
 * origin-scoped — and since every shop on this platform is its own hostname,
 * one shop can never read another's. It is deliberately NOT put in the URL,
 * where it would be copied into a shared link, a referrer header and the
 * browser history.
 *
 * <h3>A basket is created lazily</h3>
 *
 * <p>Nothing is created by looking at a page. The first Add creates the basket;
 * a visitor who browses and leaves costs no row. This also means
 * {@link CartState.cart} is null on a first visit, which is the empty state —
 * not an error, and not a reason to show a spinner.
 *
 * <h3>Every mutation returns the whole basket</h3>
 *
 * <p>The backend recomputes totals, tax and shipping options on each change and
 * returns the result, so the UI replaces its state wholesale rather than
 * patching a line and re-deriving a total. Money is never recomputed in the
 * browser; a total is what the server said it was.
 */

const TOKEN_STORAGE_KEY = 'sorsy.cart.token';

export interface CartState {
  /** The basket, or null when there is not one yet — the normal first visit. */
  cart: Cart | null;
  /** True only while a request is in flight. */
  busy: boolean;
  /** The last failure, for a block to surface. Cleared by the next success. */
  error: string | null;
  /** Whether the mounted source implements the cart at all. */
  available: boolean;
  /** Number of item lines, for a header badge. */
  lineCount: number;

  add(item: CartAddition): Promise<boolean>;
  setQuantity(lineId: number, quantity: DecimalString): Promise<void>;
  remove(lineId: number): Promise<void>;
  applyCoupon(code: string): Promise<void>;
  removeCoupon(): Promise<void>;
  /** Picks a delivery method (or collection) and re-totals the basket. */
  setFulfilment(fulfilment: CartFulfilment): Promise<void>;
  /** Re-reads the basket; used after returning to a tab left open. */
  refresh(): Promise<void>;
  /** Adopt a server-returned ordinary cart token only after this host verifies it. */
  adoptToken(token: string): Promise<boolean>;
  /** Forgets the basket locally. Called after checkout consumes it. */
  forget(): void;
}

const CartContext = createContext<CartState | null>(null);

/** Total sellable quantity for chrome badges; child modifier rows are not items. */
export function cartItemCount(lines: CartLine[] | null | undefined): number {
  return (lines ?? []).reduce((total, line) => {
    if (line.parentLineId != null) return total;
    const quantity = Number(line.quantity);
    return Number.isFinite(quantity) && quantity > 0 ? total + quantity : total;
  }, 0);
}

function readToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // Private mode, or site data blocked. The basket then lasts one page view,
    // which is worse than it should be but is not a broken shop.
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* see readToken */
  }
}

/**
 * Whether the basket a token names no longer exists.
 *
 * <p>Asked only after a mutation has already failed, so the extra round trip
 * costs nothing on the happy path. A read that itself fails answers "no": an
 * unreachable backend is not evidence that a basket is gone, and treating it as
 * such would silently abandon a shopper's basket over a dropped connection.
 */
async function gone(
  commerce: { getCart?(token: string): Promise<Cart | null> },
  token: string | null,
): Promise<boolean> {
  if (!token || !commerce.getCart) return false;
  try {
    return (await commerce.getCart(token)) === null;
  } catch {
    return false;
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const commerce = useCommerceSource();
  const [cart, setCart] = useState<Cart | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The token the hook is working with, held in a ref as well as storage so a
  // mutation started before a re-render still targets the right basket.
  const tokenRef = useRef<string | null>(null);
  const sourceRef = useRef(commerce);
  sourceRef.current = commerce;
  const sourceEpoch = useRef(0);
  const adopting = useRef(false);

  useEffect(() => {
    sourceEpoch.current++;
    setBusy(false);
    setError(null);
    setCart(null);
  }, [commerce]);

  const available = Boolean(commerce?.createCart && commerce?.getCart && commerce?.addToCart);

  const load = useCallback(async () => {
    if (!commerce?.getCart) return;
    const epoch = sourceEpoch.current;
    const token = tokenRef.current ?? readToken();
    if (!token) return;
    tokenRef.current = token;
    try {
      const found = await commerce.getCart(token);
      if (sourceRef.current !== commerce || sourceEpoch.current !== epoch) return;
      if (found) {
        setCart(found);
      } else {
        // Expired or never existed. Forget it rather than showing an error: the
        // shopper's basket is gone, which is sad, but the shop still works.
        tokenRef.current = null;
        writeToken(null);
        setCart(null);
      }
    } catch {
      // A read failure leaves the previous state alone. A transient blip should
      // not empty a basket the shopper can see.
    }
  }, [commerce]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Runs one mutation, keeping `busy`/`error` honest whatever happens. */
  const run = useCallback(
    async (operation: (token: string) => Promise<Cart>, needsCart: boolean) => {
      if (!commerce?.createCart) return false;
      const epoch = sourceEpoch.current;
      const current = () => sourceRef.current === commerce && sourceEpoch.current === epoch;
      setBusy(true);
      setError(null);
      try {
        let token = tokenRef.current ?? readToken();
        if (!token) {
          if (needsCart) return false; // nothing to act on — an empty basket has no lines
          const created = await commerce.createCart();
          if (!current()) return false;
          token = created.token;
          tokenRef.current = token;
          writeToken(token);
        }
        const next = await operation(token);
        if (!current()) return false;
        tokenRef.current = next.token;
        writeToken(next.token);
        setCart(next);
        return true;
      } catch (failure) {
        if (!current()) return false;
        // One failure is worth retrying rather than reporting: the basket the
        // token names is gone. It was checked out — in this tab or another one
        // — or swept for age, and the shopper clicking Add meant "put this in a
        // basket", not "put this in THAT basket". So confirm it really is gone,
        // start a fresh one and try once. Only once, and only for operations
        // that do not need an existing basket: retrying a quantity change on a
        // line that no longer exists would just fail again, differently.
        if (!needsCart && commerce.getCart && (await gone(commerce, tokenRef.current))) {
          if (!current()) return false;
          try {
            const created = await commerce.createCart();
            if (!current()) return false;
            tokenRef.current = created.token;
            writeToken(created.token);
            const next = await operation(created.token);
            if (!current()) return false;
            tokenRef.current = next.token;
            writeToken(next.token);
            setCart(next);
            return true;
          } catch {
            // Fall through to the original report. A second failure is a real
            // one, and a retry loop over a broken backend helps nobody.
          }
        }
        if (current()) setError(failure instanceof Error ? failure.message : 'basket update failed');
        return false;
      } finally {
        if (current()) setBusy(false);
      }
    },
    [commerce],
  );

  const value = useMemo<CartState>(() => {
    return {
      cart,
      busy,
      error,
      available,
      lineCount: cartItemCount(cart?.lines),
      add: (item) => run((token) => commerce!.addToCart!(token, item), false),
      setQuantity: async (lineId, quantity) => {
        await run((token) => commerce!.setCartLineQuantity!(token, lineId, quantity), true);
      },
      remove: async (lineId) => {
        await run((token) => commerce!.removeCartLine!(token, lineId), true);
      },
      applyCoupon: async (code) => {
        await run((token) => commerce!.applyCoupon!(token, code), true);
      },
      removeCoupon: async () => {
        await run((token) => commerce!.removeCoupon!(token), true);
      },
      setFulfilment: async (fulfilment) => {
        // `true`: there is nothing to fulfil until something is in the basket,
        // and creating one here would mean a bare visit to /checkout minting a
        // row nobody asked for.
        await run((token) => commerce!.setCartFulfilment!(token, fulfilment), true);
      },
      refresh: load,
      adoptToken: async (token) => {
        if (!commerce?.getCart || !available || busy || adopting.current || !token || token.length > 256 || token.trim() !== token) return false;
        adopting.current = true;
        const epoch = sourceEpoch.current;
        setBusy(true); setError(null);
        try {
          const verified = await commerce.getCart(token);
          if (sourceRef.current !== commerce || sourceEpoch.current !== epoch || !verified || verified.token !== token) return false;
          tokenRef.current = token;
          writeToken(token);
          setCart(verified);
          return true;
        } catch { if (sourceRef.current === commerce) setError('basket could not be verified'); return false; }
        finally { adopting.current = false; if (sourceRef.current === commerce && sourceEpoch.current === epoch) setBusy(false); }
      },
      forget: () => {
        tokenRef.current = null;
        writeToken(null);
        setCart(null);
      },
    };
  }, [cart, busy, error, available, commerce, run, load]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * The shared basket.
 *
 * <p>Returns null when no {@link CartProvider} is mounted, which is the state of
 * a quote-led site that sells nothing through a checkout. A block must render
 * its own empty state on null rather than throwing.
 */
export function useCart(): CartState | null {
  return useContext(CartContext);
}
